import { computed, type MaybeRef, unref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type {
  AdminExpertWalletBalance,
  AdminExpertWalletBalancesMeta,
  AdminExpertWalletBalancesQueryParams,
} from "@shopexperts/legacy-api";

interface UseAdminExpertWalletBalancesOptions {
  filters?: MaybeRef<AdminExpertWalletBalancesQueryParams | undefined>;
  perPage?: number;
  enabled?: MaybeRef<boolean>;
}

export function useAdminExpertWalletBalances(options: UseAdminExpertWalletBalancesOptions = {}) {
  const api = useApi();
  const perPage = options.perPage ?? 25;
  const enabled = computed(() => unref(options.enabled) ?? true);
  const normalizedFilters = computed<AdminExpertWalletBalancesQueryParams>(() => ({
    ...(unref(options.filters) ?? {}),
    per_page: perPage,
  }));

  const query = useInfiniteQuery({
    queryKey: computed(() => ["admin", "wallet", "expert-balances", normalizedFilters.value]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.wallet.listExpertWalletBalances({
        query: {
          ...normalizedFilters.value,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.has_next ? lastPage.pagination.cursor ?? undefined : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const balances = computed<AdminExpertWalletBalance[]>(() =>
    query.data.value?.pages.flatMap((page) => page.data) ?? [],
  );

  // Totals describe the whole filtered set, so any page carries the same meta.
  const meta = computed<AdminExpertWalletBalancesMeta | undefined>(
    () => query.data.value?.pages[0]?.meta,
  );

  return {
    balances,
    meta,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    query,
  };
}
